/**
 * Read the cohort out of an uploaded CSV.
 *
 * The screening endpoint parses the same file server-side, so this is not the source of
 * truth for what gets screened — it exists because the two steps need different things
 * from one upload. Screening takes the raw file; ingestion takes a JSON list of MRNs,
 * because its own CSV reader expects an OncoEMR export and ours has a single column.
 * Parsing here also lets the page show a count before anything is sent.
 */

/** The one column a cohort CSV must have, matched case- and whitespace-insensitively. */
export const MRN_COLUMN = "MRNS";

/**
 * Split one CSV line into fields, honouring double-quoted values.
 *
 * @param line - A single line of the file
 * @returns The line's fields, unquoted and trimmed
 */
function fields(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let quoted = false;
  for (const char of line) {
    if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      out.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  out.push(current);
  return out.map((field) => field.trim());
}

/**
 * Extract the de-duplicated MRNs named by a cohort CSV.
 *
 * @param text - The whole file as text
 * @returns The MRNs in first-seen order
 * @throws {Error} When the file has no MRNS column
 */
export function parseCohortMrns(text: string): string[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (!lines.length) throw new Error("The file is empty");

  const header = fields(lines[0]).map((name) => name.toUpperCase());
  const column = header.indexOf(MRN_COLUMN);
  if (column === -1) {
    throw new Error(
      `The file needs a ${MRN_COLUMN} column; it has ${header.join(", ") || "no headers"}`,
    );
  }

  const seen = new Set<string>();
  const mrns: string[] = [];
  for (const line of lines.slice(1)) {
    const mrn = fields(line)[column];
    if (mrn && !seen.has(mrn)) {
      seen.add(mrn);
      mrns.push(mrn);
    }
  }
  return mrns;
}
